import React, { ButtonHTMLAttributes, MutableRefObject, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import media from '../../constants/media';
import theme from '../../constants/theme';
import SVG from '../rawSVG';

const buttonTypes = {
	primary: {
		size: 64,
		sizeSm: 56,
		iconColor: theme.colors.white,
		backgroundColor: theme.colorsV2.mainRed,
		borderColor: theme.colorsV2.mainRed,
		disabledIconColor: theme.colors.white,
		disabledBackgroundColor: theme.colorsV2.grey5,
		disabledBorderColor: theme.colorsV2.grey5,
		hoverBackgroundColor: theme.colorsV2.secondaryRed,
		hoverBorderColor: theme.colorsV2.secondaryRed,
		hoverIconColor: theme.colors.white,
		activeIconColor: theme.colorsV2.lightRed,
		activeBackgroundColor: theme.colorsV2.lightRed,
		activeBorderColor: theme.colorsV2.lightRed,
		focusBackgroundColor: theme.colorsV2.mainRed,
		focusBorderColor: theme.colorsV2.secondaryRed,
		focusBorderWidth: 2,
		mixBlendMode: 'multiply',
	},
	secondary: {
		size: 48,
		sizeSm: 48,
		iconColor: theme.colorsV2.mainRed,
		backgroundColor: theme.colors.white,
		borderColor: theme.colorsV2.grey5,
		disabledIconColor: theme.colors.white,
		disabledBackgroundColor: theme.colorsV2.grey5,
		disabledBorderColor: theme.colorsV2.grey5,
		hoverBackgroundColor: theme.colors.white,
		hoverBorderColor: theme.colorsV2.secondaryRed,
		hoverIconColor: theme.colorsV2.secondaryRed,
		activeIconColor: theme.colorsV2.lightRed,
		activeBackgroundColor: theme.colors.white,
		activeBorderColor: theme.colorsV2.lightRed,
		focusBackgroundColor: `${theme.colorsV2.mainRed}1A`,
		focusBorderColor: theme.colorsV2.mainRed,
		focusBorderWidth: 0,
		mixBlendMode: 'multiply',
	},
};

type TButtonType = typeof buttonTypes.primary;
type TButtonTypeOption = keyof typeof buttonTypes;

const ButtonIcon = styled.div`
	transition: 0.3s ease;
	width: 21px;
	height: 21px;

	${media.tablet`
		width: 24px;
		height: 24px;
    `};
`;

const ButtonRoot = styled.button<{
	$buttonType: TButtonType;
	disabled?: boolean;
	hidden?: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	width: ${({ $buttonType }) => $buttonType.sizeSm}px;
	height: ${({ $buttonType }) => $buttonType.sizeSm}px;
	background-color: ${({ disabled, $buttonType }) =>
		disabled ? $buttonType.disabledBackgroundColor : $buttonType.backgroundColor};
	cursor: ${({ disabled }) => !disabled && 'pointer'};
	transition: all 0.3s ease;
	border-color: ${({ disabled, $buttonType }) =>
		disabled ? $buttonType.disabledBorderColor : $buttonType.borderColor};
	border-style: solid;
	border-width: 1px;

	svg path {
		stroke: ${({ disabled, $buttonType }) => (disabled ? $buttonType.disabledIconColor : $buttonType.iconColor)};
	}

	&:active {
		background-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeBackgroundColor};
		border-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeBorderColor};

		svg path {
			stroke: ${({ disabled, $buttonType }) => !disabled && $buttonType.activeIconColor};
		}
	}

	&:focus-visible {
		box-shadow: ${({ $buttonType }) =>
			`inset 0 0 0 ${$buttonType.focusBorderWidth}px ${$buttonType.focusBorderColor}`};
		background-color: ${({ $buttonType }) => $buttonType.focusBackgroundColor};
		border-color: ${({ $buttonType }) => $buttonType.focusBorderColor};
		outline: none;
	}

	${media.tablet`
		width: ${({ $buttonType }) => $buttonType.size}px;
		height: ${({ $buttonType }) => $buttonType.size}px;
    `};

	${media.desktopSmall`
		&:hover {
			background-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverBackgroundColor};
			border-color: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverBorderColor};
			box-shadow: ${({ disabled }) => !disabled && '0 0 30px rgba(0, 0, 0, 0.08)'};

			${ButtonIcon} {
				transform: ${({ disabled }) => !disabled && 'translateX(3px)'};

				svg path {
					stroke: ${({ disabled, $buttonType }) => !disabled && $buttonType.hoverIconColor};
				}
			}
		}
    `};
`;

type TProps = {
	buttonRef?: MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
	children?: ReactNode;
	iconUrl?: string;
	title?: string;
	href?: string;
	target?: string | null;
	type?: TButtonTypeOption;
	htmlType?: ButtonHTMLAttributes<any>['type'];
	disabled?: boolean;
	hidden?: boolean;
	onClick?: () => void;
	onFocus?: () => void;
	className?: string;
};

const IconButton = ({
	buttonRef,
	children,
	iconUrl,
	title,
	href,
	target,
	type = 'primary',
	htmlType,
	disabled,
	hidden,
	onClick,
	onFocus,
	className,
}: TProps) => {
	if (!href) {
		return (
			<ButtonRoot
				ref={buttonRef}
				onClick={!disabled ? onClick : undefined}
				onFocus={!disabled ? onFocus : undefined}
				$buttonType={buttonTypes[type]}
				type={htmlType}
				disabled={disabled}
				aria-hidden={hidden}
				aria-label={title}
				tabIndex={hidden ? -1 : 0}
				className={className}>
				{iconUrl && (
					<ButtonIcon>
						<SVG src={iconUrl} width={100} height={100} />
					</ButtonIcon>
				)}
				{children && <ButtonIcon>{children}</ButtonIcon>}
			</ButtonRoot>
		);
	}

	return (
		<ButtonRoot
			{...(!href.startsWith('http') &&
				(!href.endsWith('pdf') || !href.endsWith('exe') || !href.endsWith('zip')) && { as: Link, to: href })}
			{...((href.startsWith('http') || href.endsWith('pdf') || href.endsWith('exe') || href.endsWith('zip')) && {
				as: 'a',
				href: href,
				target: target,
			})}
			ref={buttonRef}
			onClick={!disabled ? onClick : undefined}
			$buttonType={buttonTypes[type]}
			disabled={disabled}
			aria-hidden={hidden}
			aria-label={title}
			tabIndex={hidden ? -1 : 0}
			className={className}>
			{iconUrl && (
				<ButtonIcon>
					<SVG src={iconUrl} width={100} height={100} />
				</ButtonIcon>
			)}
			{children && <ButtonIcon>{children}</ButtonIcon>}
		</ButtonRoot>
	);
};

export default IconButton;
